import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../../../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_requestclient_4dcc8fff from 'nuxt_plugin_requestclient_4dcc8fff' // Source: ../../../plugins/common/request.client.js (mode: 'client')
import nuxt_plugin_toastclient_2f7f0217 from 'nuxt_plugin_toastclient_2f7f0217' // Source: ../../../plugins/common/toast.client.js (mode: 'client')
import nuxt_plugin_relyclient_b2b06868 from 'nuxt_plugin_relyclient_b2b06868' // Source: ../../../plugins/common/rely.client.js (mode: 'client')
import nuxt_plugin_lazyloadclient_5e62dfa6 from 'nuxt_plugin_lazyloadclient_5e62dfa6' // Source: ../../../plugins/common/lazyload.client.js (mode: 'client')
import nuxt_plugin_components_5ca61273 from 'nuxt_plugin_components_5ca61273' // Source: ../../../plugins/common/components.js (mode: 'all')
import nuxt_plugin_gestureclient_611d4b8e from 'nuxt_plugin_gestureclient_611d4b8e' // Source: ../../../plugins/common/gesture.client.js (mode: 'client')
import nuxt_plugin_indexclient_15872525 from 'nuxt_plugin_indexclient_15872525' // Source: ../../../plugins/fetch/index.client.js (mode: 'client')
import nuxt_plugin_indexserver_7b4096ad from 'nuxt_plugin_indexserver_7b4096ad' // Source: ../../../plugins/fetch/index.server.js (mode: 'server')
import nuxt_plugin_indexclient_205e0ec0 from 'nuxt_plugin_indexclient_205e0ec0' // Source: ../../../plugins/bridge/index.client.js (mode: 'client')
import nuxt_plugin_indexclient_e36ad71a from 'nuxt_plugin_indexclient_e36ad71a' // Source: ../../../plugins/error/index.client.js (mode: 'client')
import nuxt_plugin_browserclient_3d459d58 from 'nuxt_plugin_browserclient_3d459d58' // Source: ../../../plugins/common/browser.client.js (mode: 'client')
import nuxt_plugin_ucbrowserclient_1f67700a from 'nuxt_plugin_ucbrowserclient_1f67700a' // Source: ../../../plugins/common/ucbrowser.client.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn(`<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead`)
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>`
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  const registerModule = store.registerModule
  store.registerModule = (path, rawModule, options) => registerModule.call(store, path, rawModule, Object.assign({ preserveState: process.client }, options))

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [ defaultTransition ],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [ transitions ]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error('inject(key, value) has no value provided')
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Vue.prototype.hasOwnProperty(key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (process.client && typeof nuxt_plugin_requestclient_4dcc8fff === 'function') {
    await nuxt_plugin_requestclient_4dcc8fff(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_toastclient_2f7f0217 === 'function') {
    await nuxt_plugin_toastclient_2f7f0217(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_relyclient_b2b06868 === 'function') {
    await nuxt_plugin_relyclient_b2b06868(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_lazyloadclient_5e62dfa6 === 'function') {
    await nuxt_plugin_lazyloadclient_5e62dfa6(app.context, inject)
  }

  if (typeof nuxt_plugin_components_5ca61273 === 'function') {
    await nuxt_plugin_components_5ca61273(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gestureclient_611d4b8e === 'function') {
    await nuxt_plugin_gestureclient_611d4b8e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_indexclient_15872525 === 'function') {
    await nuxt_plugin_indexclient_15872525(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_indexserver_7b4096ad === 'function') {
    await nuxt_plugin_indexserver_7b4096ad(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_indexclient_205e0ec0 === 'function') {
    await nuxt_plugin_indexclient_205e0ec0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_indexclient_e36ad71a === 'function') {
    await nuxt_plugin_indexclient_e36ad71a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_browserclient_3d459d58 === 'function') {
    await nuxt_plugin_browserclient_3d459d58(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ucbrowserclient_1f67700a === 'function') {
    await nuxt_plugin_ucbrowserclient_1f67700a(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
